import React, { useEffect, useMemo, useState } from 'react';
import { Alert, AlertDescription, AlertTitle } from '../../ui/alert';
import { Badge } from '../../ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { AlertCircle, ShieldCheck } from 'lucide-react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import * as ss from 'simple-statistics';
import { ParsedWorksheet, WorksheetColumn } from './worksheet';

interface ReliabilitySurvivalProps {
  worksheet: ParsedWorksheet;
  selectedColumnId?: string;
}

type Observation = {
  rowIndex: number;
  time: number;
  failed: boolean;
  statusLabel: string;
  stress: number | null;
};

type KaplanMeierPoint = {
  time: number;
  survival: number;
  failures: number;
  censored: number;
  atRisk: number;
};

type WeibullFit = {
  beta: number;
  eta: number;
  b10: number;
  median: number;
  mean: number;
  points: Array<{ x: number; y: number; fit: number }>;
};

type StressLifeFit = {
  slope: number;
  intercept: number;
  r2: number;
  chartData: Array<{ stress: number; lnLife: number; fitted: number; time: number }>;
  lowStressLife: number;
  highStressLife: number;
  accelerationFactor: number;
};

const NONE_VALUE = '__none__';

const formatNumber = (value: number, digits = 4) => (Number.isFinite(value) ? value.toFixed(digits) : '—');

const parseFailureStatus = (value?: string) => {
  const normalized = String(value ?? '').trim().toLowerCase();
  if (!normalized) return { failed: true, label: 'Failure' };
  if (['1', 'fail', 'failed', 'failure', 'event', 'yes', 'true'].includes(normalized)) {
    return { failed: true, label: 'Failure' };
  }
  if (['0', 'censored', 'censor', 'suspended', 'suspension', 'survived', 'alive', 'no', 'false'].includes(normalized)) {
    return { failed: false, label: 'Censored' };
  }
  return { failed: true, label: value?.trim() || 'Failure' };
};

const gammaApprox = (value: number) => {
  if (value <= 0) return Number.NaN;
  const coefficients = [
    676.5203681218851,
    -1259.1392167224028,
    771.3234287776531,
    -176.6150291621406,
    12.507343278686905,
    -0.13857109526572012,
    9.984369578019572e-6,
    1.5056327351493116e-7,
  ];

  if (value < 0.5) {
    return Math.PI / (Math.sin(Math.PI * value) * gammaApprox(1 - value));
  }

  let x = 0.9999999999998099;
  const z = value - 1;
  coefficients.forEach((coefficient, index) => {
    x += coefficient / (z + index + 1);
  });
  const t = z + coefficients.length - 0.5;
  return Math.sqrt(2 * Math.PI) * t ** (z + 0.5) * Math.exp(-t) * x;
};

const buildObservations = (
  timeColumn: WorksheetColumn | undefined,
  statusColumn: WorksheetColumn | undefined,
  stressColumn: WorksheetColumn | undefined,
): Observation[] => {
  if (!timeColumn) return [];

  return timeColumn.values.flatMap((rawTime, rowIndex) => {
    const time = Number(rawTime ?? '');
    if (!Number.isFinite(time) || time <= 0) return [];

    const status = parseFailureStatus(statusColumn?.values[rowIndex]);
    const stressValue = stressColumn ? Number(stressColumn.values[rowIndex] ?? '') : Number.NaN;

    return [{
      rowIndex: rowIndex + 1,
      time,
      failed: status.failed,
      statusLabel: status.label,
      stress: Number.isFinite(stressValue) ? stressValue : null,
    } satisfies Observation];
  });
};

const computeKaplanMeier = (observations: Observation[]) => {
  if (observations.length === 0) return [] as KaplanMeierPoint[];

  const uniqueTimes = Array.from(new Set(observations.map((observation) => observation.time))).sort((a, b) => a - b);
  let survival = 1;

  return uniqueTimes.map((time) => {
    const atRisk = observations.filter((observation) => observation.time >= time).length;
    const failures = observations.filter((observation) => observation.time === time && observation.failed).length;
    const censored = observations.filter((observation) => observation.time === time && !observation.failed).length;
    if (atRisk > 0 && failures > 0) {
      survival *= 1 - failures / atRisk;
    }
    return {
      time,
      survival,
      failures,
      censored,
      atRisk,
    } satisfies KaplanMeierPoint;
  });
};

const fitWeibull = (failureTimes: number[]): WeibullFit | null => {
  if (failureTimes.length < 3) return null;

  const sorted = [...failureTimes].sort((a, b) => a - b);
  const probabilityRows = sorted.map((time, index) => {
    const medianRank = (index + 1 - 0.3) / (sorted.length + 0.4);
    return {
      x: Math.log(time),
      y: Math.log(-Math.log(1 - medianRank)),
    };
  });

  const regression = ss.linearRegression(probabilityRows.map((point) => [point.x, point.y] as [number, number]));
  const beta = regression.m;
  if (!Number.isFinite(beta) || beta <= 0) return null;

  const eta = Math.exp(-regression.b / beta);
  if (!Number.isFinite(eta) || eta <= 0) return null;

  const b10 = eta * (-Math.log(0.9)) ** (1 / beta);
  const median = eta * Math.log(2) ** (1 / beta);
  const mean = eta * gammaApprox(1 + 1 / beta);

  return {
    beta,
    eta,
    b10,
    median,
    mean,
    points: probabilityRows.map((point) => ({
      ...point,
      fit: regression.m * point.x + regression.b,
    })),
  };
};

const predictWeibullSurvival = (time: number, fit: WeibullFit | null) => {
  if (!fit || time <= 0) return Number.NaN;
  return Math.exp(-((time / fit.eta) ** fit.beta));
};

const fitStressLife = (observations: Observation[]) => {
  const usable = observations.filter((observation) => observation.failed && observation.stress !== null && observation.time > 0);
  if (usable.length < 3) return null as StressLifeFit | null;

  const points = usable.map((observation) => [observation.stress as number, Math.log(observation.time)] as [number, number]);
  const regression = ss.linearRegression(points);
  const fitted = points.map(([stress, lnLife]) => {
    const fit = regression.m * stress + regression.b;
    return {
      stress,
      lnLife,
      fitted: fit,
      time: Math.exp(lnLife),
    };
  });

  const meanY = ss.mean(fitted.map((row) => row.lnLife));
  const sse = fitted.reduce((sum, row) => sum + (row.lnLife - row.fitted) ** 2, 0);
  const sst = fitted.reduce((sum, row) => sum + (row.lnLife - meanY) ** 2, 0);
  const lowStress = Math.min(...usable.map((observation) => observation.stress as number));
  const highStress = Math.max(...usable.map((observation) => observation.stress as number));
  const lowStressLife = Math.exp(regression.m * lowStress + regression.b);
  const highStressLife = Math.exp(regression.m * highStress + regression.b);

  return {
    slope: regression.m,
    intercept: regression.b,
    r2: sst === 0 ? 0 : 1 - sse / sst,
    chartData: fitted,
    lowStressLife,
    highStressLife,
    accelerationFactor: highStressLife === 0 ? Number.NaN : lowStressLife / highStressLife,
  };
};

export const ReliabilitySurvival: React.FC<ReliabilitySurvivalProps> = ({ worksheet, selectedColumnId }) => {
  const numericColumns = worksheet.numericColumns.filter((column) => column.numericCount >= 3);
  const categoricalColumns = worksheet.columns.filter((column) => column.id !== selectedColumnId);

  const [timeColumnId, setTimeColumnId] = useState(selectedColumnId ?? numericColumns[0]?.id ?? '');
  const [statusColumnId, setStatusColumnId] = useState<string>(NONE_VALUE);
  const [stressColumnId, setStressColumnId] = useState<string>(NONE_VALUE);
  const [missionTime, setMissionTime] = useState('100');
  const [weibayesBeta, setWeibayesBeta] = useState('2');
  const [weibayesConfidence, setWeibayesConfidence] = useState('0.9');

  useEffect(() => {
    if (!timeColumnId && numericColumns[0]) {
      setTimeColumnId(selectedColumnId && numericColumns.some((column) => column.id === selectedColumnId) ? selectedColumnId : numericColumns[0].id);
    }
  }, [numericColumns, selectedColumnId, timeColumnId]);

  const timeColumn = numericColumns.find((column) => column.id === timeColumnId) ?? numericColumns[0];
  const statusColumn = statusColumnId === NONE_VALUE ? undefined : worksheet.columns.find((column) => column.id === statusColumnId);
  const stressColumn = stressColumnId === NONE_VALUE ? undefined : numericColumns.find((column) => column.id === stressColumnId);

  const observations = useMemo(
    () => buildObservations(timeColumn, statusColumn, stressColumn),
    [statusColumn, stressColumn, timeColumn],
  );

  const failures = observations.filter((observation) => observation.failed);
  const censored = observations.filter((observation) => !observation.failed);
  const kaplanMeier = useMemo(() => computeKaplanMeier(observations), [observations]);
  const weibull = useMemo(() => fitWeibull(failures.map((observation) => observation.time)), [failures]);
  const stressLife = useMemo(() => fitStressLife(observations), [observations]);

  const missionTimeValue = Math.max(0.0001, Number(missionTime) || 0);
  const warrantyRisk = predictWeibullSurvival(missionTimeValue, weibull);
  const failureProbabilityAtMission = Number.isFinite(warrantyRisk) ? 1 - warrantyRisk : Number.NaN;

  const totalExposure = observations.reduce((sum, observation) => sum + observation.time, 0);
  const weibayesBetaValue = Math.max(0.1, Number(weibayesBeta) || 2);
  const weibayesConfidenceValue = Math.min(0.999, Math.max(0.5, Number(weibayesConfidence) || 0.9));
  const weibayesEta = totalExposure > 0
    ? (totalExposure / Math.max(failures.length, 1)) ** (1 / weibayesBetaValue)
    : Number.NaN;
  const weibayesEtaLower = totalExposure > 0
    ? (totalExposure / -Math.log(weibayesConfidenceValue)) ** (1 / weibayesBetaValue)
    : Number.NaN;
  const weibayesB10 = Number.isFinite(weibayesEta)
    ? weibayesEta * (-Math.log(0.9)) ** (1 / weibayesBetaValue)
    : Number.NaN;

  const survivalChartData = useMemo(() => {
    if (kaplanMeier.length === 0) return [] as Array<{ time: number; km: number; weibull: number | null }>;
    return kaplanMeier.map((point) => ({
      time: point.time,
      km: point.survival,
      weibull: weibull ? predictWeibullSurvival(point.time, weibull) : null,
    }));
  }, [kaplanMeier, weibull]);

  const probabilityPlotData = weibull?.points ?? [];

  if (numericColumns.length === 0) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>No numeric life column available</AlertTitle>
        <AlertDescription>Paste failure-time or time-to-event data into the worksheet to run reliability analysis.</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <Badge>Ready</Badge>
                <Badge variant="outline">Reliability</Badge>
              </div>
              <CardTitle className="mt-3 flex items-center gap-2 text-3xl">
                <ShieldCheck className="h-8 w-8 text-green-700" />
                Reliability / Survival
              </CardTitle>
              <CardDescription className="mt-2 max-w-3xl text-base">
                Analyze time-to-failure data with survival curves, Weibull modeling, warranty screening, Weibayes estimates, and a simple stress-life view.
              </CardDescription>
            </div>
            <div className="grid min-w-[15rem] grid-cols-2 gap-3 text-sm">
              <div className="rounded-xl border bg-muted/30 p-3">
                <div className="text-muted-foreground">Units / rows</div>
                <div className="mt-1 text-2xl font-semibold">{observations.length}</div>
              </div>
              <div className="rounded-xl border bg-muted/30 p-3">
                <div className="text-muted-foreground">Failures</div>
                <div className="mt-1 text-2xl font-semibold">{failures.length}</div>
              </div>
            </div>
          </div>
        </CardHeader>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Data mapping</CardTitle>
          <CardDescription>Choose the life column and optionally map status and stress columns from the worksheet.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          <div className="space-y-2">
            <Label>Time / life column</Label>
            <Select value={timeColumnId} onValueChange={setTimeColumnId}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {numericColumns.map((column) => (
                  <SelectItem key={column.id} value={column.id}>
                    {column.name} ({column.numericCount})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Status column</Label>
            <Select value={statusColumnId} onValueChange={setStatusColumnId}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE_VALUE}>Treat all rows as failures</SelectItem>
                {categoricalColumns.map((column) => (
                  <SelectItem key={column.id} value={column.id}>
                    {column.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Stress column</Label>
            <Select value={stressColumnId} onValueChange={setStressColumnId}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE_VALUE}>No stress model</SelectItem>
                {numericColumns.filter((column) => column.id !== timeColumnId).map((column) => (
                  <SelectItem key={column.id} value={column.id}>
                    {column.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {observations.length < 3 ? (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Need more life data</AlertTitle>
          <AlertDescription>The selected time column needs at least three valid positive observations.</AlertDescription>
        </Alert>
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Censored</CardTitle></CardHeader>
              <CardContent><div className="text-xl font-semibold">{censored.length}</div></CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Exposure</CardTitle></CardHeader>
              <CardContent><div className="text-xl font-semibold">{formatNumber(totalExposure, 2)}</div></CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">KM end survival</CardTitle></CardHeader>
              <CardContent><div className="text-xl font-semibold">{formatNumber((kaplanMeier.at(-1)?.survival ?? Number.NaN) * 100, 2)}%</div></CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Weibull β</CardTitle></CardHeader>
              <CardContent><div className="text-xl font-semibold">{formatNumber(weibull?.beta ?? Number.NaN)}</div></CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Characteristic life η</CardTitle></CardHeader>
              <CardContent><div className="text-xl font-semibold">{formatNumber(weibull?.eta ?? Number.NaN)}</div></CardContent>
            </Card>
          </div>

          <div className="grid gap-4 xl:grid-cols-[1.2fr_0.8fr]">
            <Card>
              <CardHeader>
                <CardTitle>Survival curve</CardTitle>
                <CardDescription>Kaplan-Meier product-limit estimate with Weibull overlay when enough failures are available.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={survivalChartData} margin={{ top: 12, right: 16, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="time" />
                      <YAxis domain={[0, 1]} tickFormatter={(value) => `${Number(value * 100).toFixed(0)}%`} />
                      <Tooltip formatter={(value: number) => `${Number(value * 100).toFixed(2)}%`} />
                      <Legend />
                      <Line type="stepAfter" dataKey="km" name="Kaplan-Meier" stroke="#16a34a" strokeWidth={2} dot />
                      <Line type="monotone" dataKey="weibull" name="Weibull fit" stroke="#2563eb" strokeWidth={2} dot={false} strokeDasharray="6 4" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Weibull summary</CardTitle>
                <CardDescription>Two-parameter Weibull fit on failed units only.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="rounded-xl border bg-muted/30 p-3">
                  <div className="text-muted-foreground">B10 life</div>
                  <div className="mt-1 text-2xl font-semibold">{formatNumber(weibull?.b10 ?? Number.NaN)}</div>
                </div>
                <div className="rounded-xl border bg-muted/30 p-3">
                  <div className="text-muted-foreground">Median life</div>
                  <div className="mt-1 text-2xl font-semibold">{formatNumber(weibull?.median ?? Number.NaN)}</div>
                </div>
                <div className="rounded-xl border bg-muted/30 p-3">
                  <div className="text-muted-foreground">Mean life</div>
                  <div className="mt-1 text-2xl font-semibold">{formatNumber(weibull?.mean ?? Number.NaN)}</div>
                </div>
                <div className="text-xs text-muted-foreground">
                  β &gt; 1 suggests wear-out behavior, β ≈ 1 looks closer to random failure, and β &lt; 1 suggests early-life issues.
                </div>
              </CardContent>
            </Card>
          </div>

          {probabilityPlotData.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Weibull probability plot</CardTitle>
                <CardDescription>Linearized failure points with fitted trend in Weibull coordinates.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <ScatterChart margin={{ top: 12, right: 20, left: 0, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="x" name="ln(time)" tickFormatter={(value) => Number(value).toFixed(2)} />
                      <YAxis dataKey="y" name="ln(-ln(1-F))" tickFormatter={(value) => Number(value).toFixed(2)} />
                      <Tooltip formatter={(value: number) => Number(value).toFixed(4)} />
                      <Legend />
                      <Scatter name="Observed" data={probabilityPlotData} fill="#16a34a" />
                      <Line type="monotone" data={probabilityPlotData} dataKey="fit" name="Fitted" stroke="#2563eb" dot={false} />
                    </ScatterChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="grid gap-4 xl:grid-cols-[0.9fr_1.1fr]">
            <Card>
              <CardHeader>
                <CardTitle>Warranty screen</CardTitle>
                <CardDescription>Estimate the chance a unit fails before a mission or warranty time using the Weibull fit.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Mission / warranty time</Label>
                  <Input value={missionTime} onChange={(event) => setMissionTime(event.target.value)} />
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="rounded-xl border bg-muted/30 p-3">
                    <div className="text-muted-foreground">Survival to time</div>
                    <div className="mt-1 text-2xl font-semibold">{formatNumber(warrantyRisk * 100, 2)}%</div>
                  </div>
                  <div className="rounded-xl border bg-muted/30 p-3">
                    <div className="text-muted-foreground">Failure before time</div>
                    <div className="mt-1 text-2xl font-semibold">{formatNumber(failureProbabilityAtMission * 100, 2)}%</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Weibayes screen</CardTitle>
                <CardDescription>Quick characteristic-life estimate using total exposure and an assumed Weibull slope.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Assumed β</Label>
                    <Input value={weibayesBeta} onChange={(event) => setWeibayesBeta(event.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Confidence</Label>
                    <Input value={weibayesConfidence} onChange={(event) => setWeibayesConfidence(event.target.value)} />
                  </div>
                </div>
                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="rounded-xl border bg-muted/30 p-3">
                    <div className="text-muted-foreground">Weibayes η</div>
                    <div className="mt-1 text-xl font-semibold">{formatNumber(weibayesEta)}</div>
                  </div>
                  <div className="rounded-xl border bg-muted/30 p-3">
                    <div className="text-muted-foreground">Zero-failure lower η</div>
                    <div className="mt-1 text-xl font-semibold">{formatNumber(weibayesEtaLower)}</div>
                  </div>
                  <div className="rounded-xl border bg-muted/30 p-3">
                    <div className="text-muted-foreground">Weibayes B10</div>
                    <div className="mt-1 text-xl font-semibold">{formatNumber(weibayesB10)}</div>
                  </div>
                </div>
                <div className="text-xs text-muted-foreground">
                  This view is a fast screening aid for assumed-slope Weibull cases. It is especially useful when failure counts are sparse and you want an immediate planning estimate.
                </div>
              </CardContent>
            </Card>
          </div>

          {stressLife ? (
            <Card>
              <CardHeader>
                <CardTitle>Accelerated stress-life view</CardTitle>
                <CardDescription>Simple log-life versus stress fit to compare expected life at lower and higher stress levels.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-4">
                  <div className="rounded-xl border bg-muted/30 p-3">
                    <div className="text-muted-foreground">Slope</div>
                    <div className="mt-1 text-xl font-semibold">{formatNumber(stressLife.slope)}</div>
                  </div>
                  <div className="rounded-xl border bg-muted/30 p-3">
                    <div className="text-muted-foreground">R²</div>
                    <div className="mt-1 text-xl font-semibold">{formatNumber(stressLife.r2)}</div>
                  </div>
                  <div className="rounded-xl border bg-muted/30 p-3">
                    <div className="text-muted-foreground">Low-stress life</div>
                    <div className="mt-1 text-xl font-semibold">{formatNumber(stressLife.lowStressLife)}</div>
                  </div>
                  <div className="rounded-xl border bg-muted/30 p-3">
                    <div className="text-muted-foreground">Acceleration factor</div>
                    <div className="mt-1 text-xl font-semibold">{formatNumber(stressLife.accelerationFactor)}</div>
                  </div>
                </div>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <ScatterChart margin={{ top: 12, right: 20, left: 0, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="stress" name="Stress" />
                      <YAxis dataKey="lnLife" name="ln(Life)" tickFormatter={(value) => Number(value).toFixed(2)} />
                      <Tooltip formatter={(value: number) => Number(value).toFixed(4)} />
                      <Legend />
                      <Scatter name="Observed ln(life)" data={stressLife.chartData} fill="#f97316" />
                      <Line type="monotone" data={stressLife.chartData} dataKey="fitted" name="Fitted ln(life)" stroke="#2563eb" dot={false} />
                    </ScatterChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Stress-life view not active yet</AlertTitle>
              <AlertDescription>
                Map a numeric stress column and provide at least three failed observations with different stress levels to screen accelerated behavior.
              </AlertDescription>
            </Alert>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Observation preview</CardTitle>
              <CardDescription>First rows loaded into the reliability workspace after column mapping and status interpretation.</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Row</TableHead>
                    <TableHead className="text-right">Time</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Stress</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {observations.slice(0, 12).map((observation) => (
                    <TableRow key={observation.rowIndex}>
                      <TableCell>{observation.rowIndex}</TableCell>
                      <TableCell className="text-right">{formatNumber(observation.time)}</TableCell>
                      <TableCell>{observation.statusLabel}</TableCell>
                      <TableCell className="text-right">{observation.stress === null ? '—' : formatNumber(observation.stress)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};
